import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  Loader2, 
  Download, 
  Plus, 
  Trash2, 
  Edit, 
  RefreshCw,
  Search,
  Car
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { adminAPI } from '../../utils/adminAPI';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminVehicleDatabase = () => {
  const [vehicles, setVehicles] = useState([]);
  const [makes, setMakes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [stats, setStats] = useState({ total: 0, makes: 0, models: 0 });

  const [formData, setFormData] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    trim: '',
    engine: '',
    transmission: '',
    drive_type: '',
    fuel_type: '',
    body_type: ''
  });

  useEffect(() => {
    fetchVehicles();
    fetchStats();
  }, []);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/vehicle-database`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicles(response.data.vehicles || []);
      setMakes(response.data.makes || []);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch vehicles',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/vehicle-database/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const syncFromNHTSA = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/vehicle-database/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: 'Success',
        description: response.data.message || 'Vehicles synced successfully'
      });

      fetchVehicles();
      fetchStats();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to sync vehicles',
        variant: 'destructive'
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('admin_token');
      
      if (editingVehicle) {
        // Update existing vehicle
        await axios.put(
          `${BACKEND_URL}/api/admin/vehicle-database/${editingVehicle.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast({ title: 'Success', description: 'Vehicle updated successfully' });
      } else {
        // Create new vehicle
        await axios.post(
          `${BACKEND_URL}/api/admin/vehicle-database`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast({ title: 'Success', description: 'Vehicle added successfully' });
      }

      setDialogOpen(false);
      resetForm();
      fetchVehicles();
      fetchStats();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save vehicle',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(
        `${BACKEND_URL}/api/admin/vehicle-database/${vehicleId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({ title: 'Success', description: 'Vehicle deleted successfully' });
      fetchVehicles();
      fetchStats();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete vehicle',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      make: vehicle.make || '',
      model: vehicle.model || '',
      year: vehicle.year || new Date().getFullYear(),
      trim: vehicle.trim || '',
      engine: vehicle.engine || '',
      transmission: vehicle.transmission || '',
      drive_type: vehicle.drive_type || '',
      fuel_type: vehicle.fuel_type || '',
      body_type: vehicle.body_type || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingVehicle(null);
    setFormData({
      make: '',
      model: '',
      year: new Date().getFullYear(),
      trim: '',
      engine: '',
      transmission: '',
      drive_type: '',
      fuel_type: '',
      body_type: ''
    });
  };

  const filteredVehicles = vehicles.filter(vehicle =>
    vehicle.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vehicle.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vehicle.year?.toString().includes(searchTerm)
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Vehicle Database Management</h1>
          <p className="text-gray-600 mt-1">
            Manage vehicle data from NHTSA database
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Vehicles</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Car className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Makes</p>
                <p className="text-2xl font-bold">{stats.makes}</p>
              </div>
              <Car className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Models</p>
                <p className="text-2xl font-bold">{stats.models}</p>
              </div>
              <Car className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex-1 w-full md:w-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by make, model, or year..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={syncFromNHTSA}
                disabled={syncing}
                variant="outline"
              >
                {syncing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Sync from NHTSA
                  </>
                )}
              </Button>

              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) resetForm();
              }}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Vehicle
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>
                      {editingVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
                    </DialogTitle>
                  </DialogHeader>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Make *</Label>
                        <Input
                          value={formData.make}
                          onChange={(e) => setFormData({...formData, make: e.target.value})}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Model *</Label>
                        <Input
                          value={formData.model}
                          onChange={(e) => setFormData({...formData, model: e.target.value})}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Year *</Label>
                        <Input
                          type="number"
                          value={formData.year}
                          onChange={(e) => setFormData({...formData, year: parseInt(e.target.value)})}
                          min="1980"
                          max="2026"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Trim</Label>
                        <Input
                          value={formData.trim}
                          onChange={(e) => setFormData({...formData, trim: e.target.value})}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Engine</Label>
                        <Input
                          value={formData.engine}
                          onChange={(e) => setFormData({...formData, engine: e.target.value})}
                          placeholder="e.g., 2.0L I4"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Transmission</Label>
                        <Input
                          value={formData.transmission}
                          onChange={(e) => setFormData({...formData, transmission: e.target.value})}
                          placeholder="e.g., Automatic"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Drive Type</Label>
                        <Input
                          value={formData.drive_type}
                          onChange={(e) => setFormData({...formData, drive_type: e.target.value})}
                          placeholder="e.g., FWD, RWD, AWD"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Fuel Type</Label>
                        <Input
                          value={formData.fuel_type}
                          onChange={(e) => setFormData({...formData, fuel_type: e.target.value})}
                          placeholder="e.g., Gasoline"
                        />
                      </div>

                      <div className="space-y-2 col-span-2">
                        <Label>Body Type</Label>
                        <Input
                          value={formData.body_type}
                          onChange={(e) => setFormData({...formData, body_type: e.target.value})}
                          placeholder="e.g., Sedan, SUV"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setDialogOpen(false);
                          resetForm();
                        }}
                      >
                        Cancel
                      </Button>
                      <Button type="submit">
                        {editingVehicle ? 'Update' : 'Add'} Vehicle
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vehicles Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            Vehicles ({filteredVehicles.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : filteredVehicles.length === 0 ? (
            <Alert>
              <AlertDescription>
                No vehicles found. Click "Sync from NHTSA" to import vehicle data.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Year</TableHead>
                    <TableHead>Make</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead>Trim</TableHead>
                    <TableHead>Engine</TableHead>
                    <TableHead>Fuel Type</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVehicles.map((vehicle) => (
                    <TableRow key={vehicle.id}>
                      <TableCell>{vehicle.year}</TableCell>
                      <TableCell className="font-medium">{vehicle.make}</TableCell>
                      <TableCell>{vehicle.model}</TableCell>
                      <TableCell>{vehicle.trim || '-'}</TableCell>
                      <TableCell>{vehicle.engine || '-'}</TableCell>
                      <TableCell>{vehicle.fuel_type || '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(vehicle)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDelete(vehicle.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminVehicleDatabase;
